import { useState, type ReactNode } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy } from '@abastece/shared';
import { esDeudaAnterior, pendienteDeCompra, type Compra } from '@abastece/core';
import { db } from '../db/dexie.js';
import type { PagoHecho } from '../lib/historialDinero.js';
import { leerPagosHechos } from '../lib/leerPagos.js';
import { cuentaDeProveedor } from '../lib/proveedores.js';
import { enlaceWhatsApp } from '../lib/recibo.js';
import { usePuede } from '../lib/sesion.js';
import { useNombreProducto } from '../i18n/productos.js';
import { EditarProveedor } from '../ui/FormularioProveedor.js';
import { FilaPagoProveedor, diaDeLista } from '../ui/FilasDeDinero.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT } from '../i18n/index.js';
import { DetalleCompra } from './Compras.js';

/** How many purchases, and payments, show before «Ver más»: a supplier of years has hundreds. */
const A_LA_VISTA = 20;

/**
 * Un proveedor (the owner, 2026-10-16: «In the suppliers page you should be able to click a
 * supplier and see all their details, and also the option to edit their information if you are the
 * owner»). What is known of them, the phone a tap from a call or a chat; what was bought from them,
 * each purchase opening on what it brought, as in «Mercadería comprada», whose actions stay there;
 * and for the owner, whose «Gastos y pagos» it is, what is still owed to them and what was paid.
 * «Editar», the owner's (`editar_proveedores`), opens the four fields in a sheet over the page.
 */
export function ProveedorDetalle() {
  const t = useT();
  const nombre = useNombreProducto();
  const { proveedorId } = useParams({ from: '/proveedores/$proveedorId' });
  // null: there is no such supplier here; undefined: Dexie has not answered yet.
  const proveedor = useLiveQuery(async () => (await db.proveedores.get(proveedorId)) ?? null, [proveedorId], undefined);
  const compras = useLiveQuery(() => db.compras.where('proveedorId').equals(proveedorId).toArray(), [proveedorId], undefined);
  const gastos = useLiveQuery(() => db.gastos.toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const puedeEditar = usePuede('editar_proveedores');
  // What is owed and what was paid are «Gastos y pagos»' (`ver_reportes`): the owner's, as there.
  const verDinero = usePuede('ver_reportes');
  const pagos = useLiveQuery(
    async () => (verDinero ? (await leerPagosHechos()).filter((p) => p.proveedorId === proveedorId) : []),
    [verDinero, proveedorId],
    [],
  );
  const [editando, setEditando] = useState(false);
  /** An id, not the purchase: the sheet reads it live, as «Mercadería comprada»'s does. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const [comprasALaVista, setComprasALaVista] = useState(A_LA_VISTA);
  const [pagosALaVista, setPagosALaVista] = useState(A_LA_VISTA);

  if (proveedor === undefined || compras === undefined) return null;
  if (proveedor === null) {
    return (
      <Pagina titulo={t('prov.titulo')} volverA="/proveedores">
        <Vacio mensaje={t('prov.noEsta')} icono="proveedores" />
      </Pagina>
    );
  }

  const cuenta = cuentaDeProveedor(proveedorId, compras, gastos, hoy());
  const productoDe = new Map(productos.map((p) => [p.id, p]));
  /** What a purchase brought, each product once: «Arroz 80/20, Aceite vegetal». */
  const loQueTrajo = (c: Compra) =>
    [
      ...new Set(
        c.lineas.map((l) => {
          const p = productoDe.get(l.productoId);
          return p ? nombre(p) : '—';
        }),
      ),
    ].join(', ');
  /** What a payment was for, as «Pagos a proveedores» says it: the invoice, the purchase's day, the expense. */
  const paraQue = (p: PagoHecho) => {
    const compra = p.deuda.tipo === 'compra' ? compras.find((c) => c.id === p.deuda.id) : undefined;
    if (compra) {
      if (esDeudaAnterior(compra)) return t('pagosProv.deudaAnterior');
      return compra.documento
        ? t('pagosProv.factura', { documento: compra.documento })
        : t('pagosProv.compraDel', { fecha: diaDeLista(compra.fecha) });
    }
    return gastos.find((g) => g.id === p.deuda.id)?.descripcion ?? t('gastos.proveedor');
  };
  const tinte = cuenta.vencido ? 'rosa' : 'ambar';

  return (
    <Pagina
      titulo={proveedor.nombre}
      subtitulo={[proveedor.activo ? null : t('prov.desactivado'), t('prov.compras', { n: cuenta.resumen.n })]
        .filter(Boolean)
        .join(' · ')}
      volverA="/proveedores"
      acciones={
        puedeEditar && (
          <button
            type="button"
            className="btn-secundario gap-1.5 px-4 text-sm"
            onClick={() => setEditando(true)}
            data-proveedor-editar
          >
            <Ico icono="editar" className="size-4" />
            {t('prov.editar')}
          </button>
        )
      }
    >
      <section className="tarjeta mb-4 flex flex-col gap-4" data-proveedor-datos>
        <Dato etiqueta={t('comun.telefono')} campo="telefono">
          {proveedor.telefono}
        </Dato>
        {proveedor.telefono && (
          <div className="-mt-1 flex gap-2">
            <a
              className="btn-secundario h-11 min-h-0 min-w-0 flex-1 gap-1.5 px-3 text-sm"
              href={`tel:${proveedor.telefono.replace(/[^\d+]/g, '')}`}
              data-proveedor-llamar
            >
              <Ico icono="telefono" className="size-4 shrink-0" />
              {t('prov.llamar')}
            </a>
            <a
              className="btn-secundario h-11 min-h-0 min-w-0 flex-1 gap-1.5 px-3 text-sm"
              href={enlaceWhatsApp('', proveedor.telefono)}
              target="_blank"
              rel="noreferrer"
              data-proveedor-whatsapp
            >
              <Ico icono="whatsapp" className="size-4 shrink-0" />
              WhatsApp
            </a>
          </div>
        )}
        <Dato etiqueta={t('prov.contacto')} campo="contacto">
          {proveedor.contacto}
        </Dato>
        <Dato etiqueta={t('comun.nota')} campo="notas">
          {proveedor.notas}
        </Dato>
      </section>

      {/*
        What is owed to them, to be paid where every account is: «Cuentas por pagar». The amount on
        a line of its own, the card's whole width: a supplier's runs to tens of thousands, which
        beside the chip broke in two at 390 px.
      */}
      {verDinero &&
        (cuenta.debe > 0 ? (
          <Link
            to="/gastos"
            search={{ vista: 'porPagar' }}
            className={`tarjeta-accion mb-4 flex flex-col gap-2 p-4 ${cuenta.vencido ? 'ring-rose-500/25' : 'ring-amber-500/25'}`}
            data-proveedor-debe
          >
            <span className="flex items-center gap-3">
              <Chip icono="pagar" tinte={tinte} />
              <span className="min-w-0 flex-1 text-sm text-slate-400">{t('prov.seLeDebe')}</span>
              <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
            </span>
            <span className="tabular text-3xl font-bold whitespace-nowrap max-[359px]:text-2xl" data-proveedor-debe-monto>
              {formatMoney(cuenta.debe)}
            </span>
            <span className={`text-xs ${cuenta.vencido ? 'text-rose-300' : 'text-slate-400'}`}>
              {cuenta.vencido ? t('gastos.hayVencidas') : t('prov.verPorPagar')}
            </span>
          </Link>
        ) : (
          <section className="tarjeta mb-4 flex items-center gap-4" data-proveedor-debe>
            <Chip icono="pagar" />
            <span className="text-sm text-slate-400">{t('prov.noSeLeDebe')}</span>
          </section>
        ))}

      <h2 className="rotulo">{t('prov.comprasTitulo')}</h2>
      {cuenta.compras.length === 0 ? (
        <Vacio mensaje={t('prov.ningunaCompra')} icono="compras" />
      ) : (
        <>
          <p className="-mt-1 mb-3 text-xs text-slate-500" data-proveedor-resumen>
            {t('prov.comprado', { n: cuenta.resumen.n, monto: formatMoney(cuenta.resumen.total) })}
            {cuenta.ultima && ` · ${t('prov.ultima', { fecha: diaDeLista(cuenta.ultima) })}`}
          </p>
          <ul className="flex flex-col gap-2" data-proveedor-compras>
            {cuenta.compras.slice(0, comprasALaVista).map((c) => {
              const pendiente = pendienteDeCompra(c);
              return (
                <li key={c.id} className={`tarjeta ${c.anulada ? 'opacity-50' : ''}`} data-proveedor-compra={c.id}>
                  {/* The purchase opens on what it brought; returning or correcting it stays in «Mercadería comprada». */}
                  <button
                    type="button"
                    onClick={() => setAbierta(c.id)}
                    data-sin-icono="fila"
                    className="tarjeta-abrir flex w-full items-start justify-between gap-4 text-left"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {diaDeLista(c.fecha)}
                        {c.documento && <span className="ml-2 text-xs text-slate-500">{c.documento}</span>}
                      </span>
                      <span className="block truncate text-sm text-slate-300">{loQueTrajo(c)}</span>
                      {c.anulada ? (
                        <span className="mt-1 block text-xs text-rose-400">
                          {t('compras.anulada', { motivo: c.motivoAnulacion ?? '' })}
                        </span>
                      ) : (
                        pendiente > 0 && (
                          <span className="mt-1 block text-xs text-amber-300">
                            {t('prov.debeDeEsta', { monto: formatMoney(pendiente) })}
                            {c.vencePago && ` · ${t('compras.vence', { fecha: c.vencePago })}`}
                          </span>
                        )
                      )}
                    </span>
                    <span className="tabular shrink-0 font-semibold">{formatMoney(c.totalCordobas)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {cuenta.compras.length > comprasALaVista && (
            <button
              type="button"
              className="btn-texto mt-2 w-full gap-1 text-sm text-slate-400"
              onClick={() => setComprasALaVista((n) => n + A_LA_VISTA * 2)}
            >
              <Ico icono="desplegar" className="size-4" />
              {t('comun.verMas')}
            </button>
          )}
        </>
      )}

      {verDinero && (
        <>
          <h2 className="rotulo mt-6">{t('prov.pagos')}</h2>
          {pagos.length === 0 ? (
            <Vacio mensaje={t('prov.ningunPago')} icono="pagar" />
          ) : (
            <>
              <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1 text-sm" data-proveedor-pagos>
                {pagos.slice(0, pagosALaVista).map((p) => (
                  <FilaPagoProveedor
                    key={p.id}
                    pago={p}
                    nombre={paraQue(p)}
                    detalle={[t(`pago.${p.formaPago}`), p.referencia ?? ''].filter(Boolean).join(' · ')}
                    className="py-2.5"
                  />
                ))}
              </ul>
              {pagos.length > pagosALaVista && (
                <button
                  type="button"
                  className="btn-texto mt-2 w-full gap-1 text-sm text-slate-400"
                  onClick={() => setPagosALaVista((n) => n + A_LA_VISTA * 2)}
                >
                  <Ico icono="desplegar" className="size-4" />
                  {t('comun.verMas')}
                </button>
              )}
            </>
          )}
        </>
      )}

      {abierta && <DetalleCompra compraId={abierta} onCerrar={() => setAbierta(null)} />}

      {/* Its own sheet, the page as it was behind it: forms in a sheet, not above the rest (the owner, 2026-10-06). */}
      {editando && (
        <Modal titulo={t('prov.editarTitulo')} onCerrar={() => setEditando(false)}>
          <EditarProveedor proveedor={proveedor} onGuardado={() => setEditando(false)} />
        </Modal>
      )}
    </Pagina>
  );
}

/** One thing known of the supplier, and «Sin anotar» where nothing is. */
function Dato({ etiqueta, campo, children }: { etiqueta: string; campo: string; children: ReactNode }) {
  const t = useT();
  return (
    <div data-proveedor-dato={campo}>
      <span className="etiqueta">{etiqueta}</span>
      {children ? (
        <p className="text-sm wrap-break-word">{children}</p>
      ) : (
        <p className="text-sm text-slate-500">{t('prov.sinAnotar')}</p>
      )}
    </div>
  );
}
